import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams, useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../AuthContext";
import { Select } from 'antd';
import "../../Activity.css";
import CallDetailsPage from "./CallDetailsPage";
import { getUserTeamIds, ROLES } from '../../utils/roleUtils';

// Special phone numbers that should be visible to all users regardless of role
// These records will bypass team-based filtering
const SPECIAL_PHONE_NUMBERS = ['40373800880', '+40373800880'];

// Helper to check if a call involves a special phone number
const isSpecialPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return false;
  const normalized = phoneNumber.replace(/\s+/g, '');
  return SPECIAL_PHONE_NUMBERS.some(special => 
    normalized === special || normalized.endsWith(special)
  );
};

const AdminActivityPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { userId } = useParams();
  const navigate = useNavigate();
  const { supabase, userProfile } = useAuth();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 480);
  const [filters, setFilters] = useState({
    direction: searchParams.get("direction") || "",
    search: searchParams.get("search") || "",
    startDate: searchParams.get("start") || "",
    endDate: searchParams.get("end") || "",
    users: [],
    teams: [],
  });
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCallId, setSelectedCallId] = useState(searchParams.get("callId") || null);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCalls, setTotalCalls] = useState(0);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [sortBy, setSortBy] = useState('started_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [tempDateRange, setTempDateRange] = useState({
    startDate: "",
    endDate: ""
  });
  const refreshIntervalRef = useRef(null);
  const CALLS_PER_PAGE = 50;

  // No frontend filtering needed - backend already filters based on role
  // Users and teams from filtered endpoints are pre-filtered by role

  const isTeamLead = userProfile?.role === ROLES.TEAM_LEAD;
  const isAdmin = userProfile?.role === ROLES.ADMIN;

  // Removed auto-select behavior - default view shows all teams for team leads

  useEffect(() => {
    // Wait for userProfile to be fully loaded before fetching users and teams
    if (!userProfile || !userProfile.id) {
      return;
    }
    
    async function fetchUsersAndTeams() {
      // Double check userProfile.id exists before making API calls
      if (!userProfile?.id) {
        console.warn('[ACTIVITY] Skipping users/teams fetch - userProfile.id not available');
        return;
      }
      
      setUsersLoading(true);
      setTeamsLoading(true);
      try {
        // Fetch users and teams with role-based filtering
        const [usersResponse, teamsResponse] = await Promise.all([
          fetch(`/api/users/filtered?userId=${userProfile.id}`),
          fetch(`/api/teams/filtered?userId=${userProfile.id}`)
        ]);
        
        // Check if responses are OK before trying to parse JSON
        if (!usersResponse.ok) {
          console.error('[ACTIVITY] Users API error:', usersResponse.status, usersResponse.statusText);
          const errorText = await usersResponse.text();
          console.error('[ACTIVITY] Users error response:', errorText);
          throw new Error(`Users API failed: ${usersResponse.status}`);
        }
        
        if (!teamsResponse.ok) {
          console.error('[ACTIVITY] Teams API error:', teamsResponse.status, teamsResponse.statusText);
          const errorText = await teamsResponse.text();
          console.error('[ACTIVITY] Teams error response:', errorText);
          throw new Error(`Teams API failed: ${teamsResponse.status}`);
        }
        
        const usersResult = await usersResponse.json();
        const teamsResult = await teamsResponse.json();
        
        if (usersResult.success) {
          setUsers(usersResult.data || []);
        }
        setUsersLoading(false);
        
        if (teamsResult.success) {
          setTeams(teamsResult.data || []);
        }
        setTeamsLoading(false);
      } catch (error) {
        console.error('[ACTIVITY] Error fetching users/teams:', error);
        // Fallback to direct database queries
        try {
          const { data: usersData } = await supabase.from('imported_users').select('id, full_name, team_id, role');
          const { data: teamsData } = await supabase.from('teams').select('id, name');
          setUsers(usersData || []);
          setTeams(teamsData || []);
        } catch (fallbackError) {
          console.error('[ACTIVITY] Fallback database queries also failed:', fallbackError);
        }
        setUsersLoading(false);
        setTeamsLoading(false);
      }
    }
    fetchUsersAndTeams();
  }, [supabase, userProfile]);

  useEffect(() => {
    const params = {};
    if (filters.direction) params.direction = filters.direction;
    if (filters.search) params.search = filters.search;
    if (filters.startDate) params.start = filters.startDate;
    if (filters.endDate) params.end = filters.endDate;
    if (selectedCallId) params.callId = selectedCallId;
    setSearchParams(params);
  }, [filters, selectedCallId, setSearchParams]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 480);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchCalls = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      let query = supabase.from('call_logs').select('id, from_number, to_number, direction, status, started_at, duration_seconds', { count: 'exact' });
      
      // Apply role-based filtering first
      // Note: Special phone numbers (RIA) are included via OR condition to bypass team filtering
      if (userProfile?.role === ROLES.TEAM_LEAD) {
        // Team leads can see calls from all their teams by default
        const teamIds = getUserTeamIds(userProfile);
        
        if (teamIds.length > 0) {
          // Build team filter with special phone number inclusion
          const teamIdsStr = filters.teams && filters.teams.length > 0 
            ? filters.teams.join(',') 
            : teamIds.join(',');
          
          // Use OR to include: (team_id in teamIds) OR (from_number is special) OR (to_number is special)
          const specialNumbersFilter = SPECIAL_PHONE_NUMBERS.map(num => 
            `from_number.ilike.%${num}%,to_number.ilike.%${num}%`
          ).join(',');
          
          query = query.or(`team_id.in.(${teamIdsStr}),${specialNumbersFilter}`);
          
          // Team leads can also filter by specific users within their teams
          if (filters.users && filters.users.length > 0) {
            query = query.in('user_id', filters.users);
          }
        } else {
          // If team lead has no teams, still show special phone number calls
          const specialNumbersFilter = SPECIAL_PHONE_NUMBERS.map(num => 
            `from_number.ilike.%${num}%,to_number.ilike.%${num}%`
          ).join(',');
          query = query.or(specialNumbersFilter);
        }
      } else if (userProfile?.role === ROLES.ADMIN) {
        // Admins can filter by users and teams as before
        if (filters.users && filters.users.length > 0) {
          query = query.in('user_id', filters.users);
        }
        if (filters.teams && filters.teams.length > 0) {
          query = query.in('team_id', filters.teams);
        }
      }
      
      // Direction filter - FIXED LOGIC
      if (filters.direction === "missed") {
        query = query.eq("status", "missed");
      } else if (filters.direction === "inbound") {
        query = query.eq("direction", "inbound").neq("status", "missed");
      } else if (filters.direction === "outbound") {
        query = query.eq("direction", "outbound").neq("status", "missed");
      } else if (filters.direction === "ria") {
        // Filter for calls involving RIA special phone numbers
        const riaFilter = SPECIAL_PHONE_NUMBERS.map(num => 
          `from_number.ilike.%${num}%,to_number.ilike.%${num}%`
        ).join(',');
        query = query.or(riaFilter);
      }
      
      // Search filter
      if (filters.search) {
        query = query.or(`from_number.ilike.%${filters.search}%,to_number.ilike.%${filters.search}%,id.ilike.%${filters.search}%`);
      }
      
      // Date range
      let startDate = filters.startDate;
      let endDate = filters.endDate;
      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        endDate = end.toISOString();
      }
      if (startDate) {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        startDate = start.toISOString();
      }
      if (startDate) query = query.gte("started_at", startDate);
      if (endDate) query = query.lte("started_at", endDate);
      
      // Sorting
      query = query.order(sortBy, { ascending: sortOrder === 'asc',  nullsFirst: false });
      
      // Pagination
      const offset = (currentPage - 1) * CALLS_PER_PAGE;
      query = query.range(offset, offset + CALLS_PER_PAGE - 1);
      
      const { data, count, error } = await query;
      
      if (error) {
        console.error("Error fetching calls:", error);
        setCalls([]);
        setTotalCalls(0);
      } else {
        setCalls(data || []);
        setTotalCalls(count || 0);
      }
      
      if (!silent) setLoading(false);
    } catch (error) {
      console.error("Error in fetchCalls:", error);
      setCalls([]);
      setTotalCalls(0);
      if (!silent) setLoading(false);
    }
  }, [supabase, filters, currentPage, sortBy, sortOrder, userProfile]);

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when filters change
  }, [filters, sortBy, sortOrder]);

  useEffect(() => {
    // Wait for userProfile to be fully loaded before fetching calls
    if (!userProfile || !userProfile.id) {
      return;
    }
    fetchCalls();
  }, [filters, currentPage, sortBy, sortOrder, userProfile, fetchCalls]);

  useEffect(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }
    const hasPendingCalls = calls.some(call => 
      call.recording_url === "pending" || 
      call.openai_transcript === "pending" ||
      (!call.recording_url && call.status === "completed" && call.duration_seconds > 0)
    );
    if (hasPendingCalls) {
      refreshIntervalRef.current = setInterval(() => {
        fetchCalls(true);
      }, 5000);
    }
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [calls, fetchCalls]);

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if clicking on date range button itself
      if (event.target.closest('.date-filter-btn')) {
        return; // Let the button handle its own toggle
      }
      
      // Check if clicking on sort button itself  
      if (event.target.closest('.sort-btn')) {
        return; // Let the button handle its own toggle
      }
      
      // Close dropdowns if clicking outside their containers
      if (showDatePicker && !event.target.closest('.date-range-container')) {
        setShowDatePicker(false);
      }
      if (showSortMenu && !event.target.closest('.sort-container')) {
        setShowSortMenu(false);
      }
    };

    if (showDatePicker || showSortMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDatePicker, showSortMenu]);

  const formatDuration = (seconds) => {
    if (!seconds) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const isToday = date.toDateString() === today.toDateString();
    const isYesterday = date.toDateString() === yesterday.toDateString();
    
    if (isToday) {
      return `Today, ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (isYesterday) {
      return `Yesterday, ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleDateString('en-US', { 
        day: '2-digit', 
        month: 'short',
        hour: '2-digit', 
        minute: '2-digit' 
      });
    }
  };

  const getCallTypeBadge = (call) => {
    // Normalize status for comparison
    const normalizedStatus = call.status ? call.status.replace(/-/g, '_') : '';
    
    // Show missed_inbound as Inbound to avoid confusion (only truly missed calls show as Missed)
    if (normalizedStatus === "missed") {
      return <span className="call-badge missed">Missed</span>;
    }
    if (call.direction === "inbound" || normalizedStatus === "missed_inbound") {
      return <span className="call-badge inbound">Inbound</span>;
    }
    if (call.direction === "outbound") {
      return <span className="call-badge outbound">Outbound</span>;
    }
    return <span className="call-badge">Unknown</span>;
  };

  // Check if a phone number is a special RIA number and render with badge
  const renderPhoneWithRiaBadge = (phoneNumber) => {
    const isRia = isSpecialPhoneNumber(phoneNumber);
    return (
      <span className="phone-with-badge">
        {phoneNumber || '-'}
        {isRia && <span className="ria-badge">RIA</span>}
      </span>
    );
  };

  const getCallStatusBadge = (status) => {
    // Normalize status: convert dashes to underscores for consistent handling
    const normalizedStatus = status ? status.replace(/-/g, '_') : '';
    
    const statusMap = {
      'completed': { label: 'Completed', class: 'completed' },
      'missed': { label: 'Missed', class: 'missed' },
      'missed_inbound': { label: 'Completed', class: 'completed' }, // Show as Completed to avoid confusion
      'in_progress': { label: 'In Progress', class: 'in-progress' },
      'initiated': { label: 'Initiated', class: 'initiated' },
      'ringing': { label: 'Ringing', class: 'ringing' },
      'answered': { label: 'Answered', class: 'answered' },
      'failed': { label: 'Failed', class: 'failed' },
      'canceled': { label: 'Canceled', class: 'canceled' },
      'cancelled': { label: 'Cancelled', class: 'canceled' },
      'busy': { label: 'Busy', class: 'busy' },
      'no_answer': { label: 'No Answer', class: 'no-answer' }
    };
    
    const statusInfo = statusMap[normalizedStatus] || { label: status || 'Unknown', class: 'unknown' };
    return <span className={`status-badge ${statusInfo.class}`}>{statusInfo.label}</span>;
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const handleCallClick = (call) => {
    setSelectedCallId(call.id);
    navigate(`/app/${userId}/activity/${call.id}`);
  };

  const handleDateRangeToggle = () => {
    if (!showDatePicker) {
      // Initialize temp state with current filter values when opening
      setTempDateRange({
        startDate: filters.startDate,
        endDate: filters.endDate
      });
    }
    setShowDatePicker(!showDatePicker);
    if (showSortMenu) setShowSortMenu(false); // Close sort menu if open
  };

  const handleSortToggle = () => {
    setShowSortMenu(!showSortMenu);
    if (showDatePicker) setShowDatePicker(false); // Close date picker if open
  };

  const handleDateRangeApply = () => {
    setFilters({ 
      ...filters, 
      startDate: tempDateRange.startDate, 
      endDate: tempDateRange.endDate 
    });
    setShowDatePicker(false);
  };

  const clearDateRange = () => {
    setTempDateRange({ startDate: "", endDate: "" });
    setFilters({ ...filters, startDate: "", endDate: "" });
    setShowDatePicker(false);
  };

  const handleTempDateChange = (key, value) => {
    setTempDateRange({ ...tempDateRange, [key]: value });
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setShowSortMenu(false);
  };

  const getSortDisplay = (field) => {
    const isActive = sortBy === field;
    const arrow = isActive ? (sortOrder === 'desc' ? '↓' : '↑') : '';
    
    const labels = {
      'started_at': 'Date',
      'duration_seconds': 'Duration', 
      'direction': 'Type'
    };
    
    return (
      <span>
        {labels[field]}
        {arrow && <span className="sort-arrow">{arrow}</span>}
      </span>
    );
  };

  const totalPages = Math.ceil(totalCalls / CALLS_PER_PAGE);

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 7;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        pages.push(1, 2, 3, 4, 5, '...', totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }

    // Calculate the range of items being shown
    const startItem = (currentPage - 1) * CALLS_PER_PAGE + 1;
    const endItem = Math.min(currentPage * CALLS_PER_PAGE, totalCalls);

    return (
      <div className="pagination">
        <div className="pagination-info">
          Showing {startItem}-{endItem} of {totalCalls} calls
        </div>
        
        <div className="pagination-controls">
          <button 
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            Previous
          </button>
          
          {pages.map((page, index) => (
            page === '...' ? (
              <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span>
            ) : (
              <button
                key={`page-${page}`}
                onClick={() => setCurrentPage(page)}
                className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
              >
                {page}
              </button>
            )
          ))}
          
          <button 
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  if (selectedCallId) {
    return (
      <CallDetailsPage
        callId={selectedCallId}
        onBack={() => {
          setSelectedCallId(null);
          navigate(`/app/${userId}/activity?${searchParams.toString()}`);
        }}
        onViewAllWithNumber={(number) => {
          setSelectedCallId(null);
          setFilters({ ...filters, search: number });
          navigate(`/app/${userId}/activity?search=${encodeURIComponent(number)}`);
        }}
      />
    );
  }

  return (
    <div className="activity-container-new">
      {/* Overlay for closing dropdowns */}
      {(showDatePicker || showSortMenu) && (
        <div 
          className="dropdown-overlay" 
          onClick={() => {
            setShowDatePicker(false);
            setShowSortMenu(false);
          }}
        />
      )}
      
      <div className="activity-header-new">
        <h2>{isTeamLead ? 'Team Call Activity' : 'Admin Call Activity'}</h2>
        <p>{isTeamLead ? 'See records of your team\'s calls' : 'See records of all recorded calls'}</p>
      </div>

      {/* Filter Tabs / Dropdown */}
      {isMobile ? (
        <div className="filter-dropdown-mobile">
          <Select
            value={filters.direction || "all"}
            onChange={(value) => handleFilterChange("direction", value === "all" ? "" : value)}
            style={{ width: "100%", marginBottom: 24 }}
            size="large"
          >
            <Select.Option value="all">All Calls</Select.Option>
            <Select.Option value="inbound">Inbound</Select.Option>
            <Select.Option value="outbound">Outbound</Select.Option>
            <Select.Option value="missed">Missed</Select.Option>
            <Select.Option value="ria">RIA</Select.Option>
          </Select>
        </div>
      ) : (
        <div className="filter-tabs">
          <button 
            className={filters.direction === "" ? "tab active" : "tab"}
            onClick={() => handleFilterChange("direction", "")}
          >
            All
          </button>
          <button 
            className={filters.direction === "inbound" ? "tab active" : "tab"}
            onClick={() => handleFilterChange("direction", "inbound")}
          >
            Inbound
          </button>
          <button 
            className={filters.direction === "outbound" ? "tab active" : "tab"}
            onClick={() => handleFilterChange("direction", "outbound")}
          >
            Outbound
          </button>
          <button 
            className={filters.direction === "missed" ? "tab active" : "tab"}
            onClick={() => handleFilterChange("direction", "missed")}
          >
            Missed
          </button>
          <button 
            className={filters.direction === "ria" ? "tab active" : "tab"}
            onClick={() => handleFilterChange("direction", "ria")}
          >
            RIA
          </button>
        </div>
      )}

      {/* Search and Filters Row */}
      <div className="filters-row">
        <div className="search-input-wrapper">
          <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19.6 21L13.3 14.7C12.8 15.1 12.225 15.4167 11.575 15.65C10.925 15.8833 10.2333 16 9.5 16C7.68333 16 6.146 15.3707 4.888 14.112C3.63 12.8533 3.00067 11.316 3 9.5C3 7.68333 3.62933 6.146 4.888 4.888C6.14667 3.63 7.684 3.00067 9.5 3C11.3167 3 12.854 3.62933 14.112 4.888C15.37 6.14667 15.9993 7.684 16 9.5C16 10.2333 15.8833 10.925 15.65 11.575C15.4167 12.225 15.1 12.8 14.7 13.3L21 19.6L19.6 21ZM9.5 14C10.75 14 11.8127 13.5627 12.688 12.688C13.5633 11.8133 14.0007 10.7507 14 9.5C14 8.25 13.5627 7.18733 12.688 6.312C11.8133 5.43667 10.7507 4.99933 9.5 5C8.25 5 7.18733 5.43733 6.312 6.312C5.43667 7.18667 4.99933 8.24933 5 9.5C5 10.75 5.43733 11.8127 6.312 12.688C7.18667 13.5633 8.24933 14.0007 9.5 14Z" fill="currentColor"/>
          </svg>
          <input
            type="text"
            placeholder="Search Call ID or Phone Number"
            value={filters.search}
            onChange={(e) => handleFilterChange("search", e.target.value)}
            className="search-input-new"
          />
          {filters.search && (
            <button
              type="button"
              onClick={() => handleFilterChange("search", "")}
              className="search-clear-btn"
              title="Clear search"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
        </div>

        {/* User Filter - show for both admins and team leads */}
        {(isAdmin || isTeamLead) && (
          <Select
            placeholder="Filter by user(s)"
            value={filters.users || []}
            onChange={(value) => handleFilterChange("users", value || [])}
            mode="multiple"
            allowClear
            showSearch
            loading={usersLoading}
            disabled={usersLoading}
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            maxTagCount={2}
            maxTagTextLength={10}
            notFoundContent={usersLoading ? 'Loading users...' : 'No users found'}
          >
            {users.map(user => (
              <Select.Option key={user.id} value={user.id}>{user.full_name}</Select.Option>
            ))}
          </Select>
        )}

        {/* Team Filter - show for both admins and team leads */}
        {(isAdmin || isTeamLead) && (
          <Select
            placeholder="Filter by team(s)"
            value={filters.teams || []}
            onChange={(value) => handleFilterChange("teams", value || [])}
            mode="multiple"
            allowClear
            showSearch
            loading={teamsLoading}
            disabled={teamsLoading}
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            maxTagCount={2}
            maxTagTextLength={10}
            notFoundContent={teamsLoading ? 'Loading teams...' : 'No teams found'}
          >
            {teams.map(team => (
              <Select.Option key={team.id} value={team.id}>{team.name}</Select.Option>
            ))}
          </Select>
        )}
        
        {/* Date Range and Sort Buttons Group */}
        <div className="button-group">
          {/* Date Range Picker */}
          <div className="date-range-container">
            <button 
              className={`date-filter-btn ${showDatePicker ? 'active' : ''} ${(filters.startDate || filters.endDate) ? 'filtered' : ''}`}
              onClick={handleDateRangeToggle}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 22Q4.175 22 3.588 21.413Q3 20.825 3 20V6Q3 5.175 3.588 4.588Q4.175 4 5 4H6V3Q6 2.575 6.288 2.287Q6.575 2 7 2Q7.425 2 7.713 2.287Q8 2.575 8 3V4H16V3Q16 2.575 16.288 2.287Q16.575 2 17 2Q17.425 2 17.713 2.287Q18 2.575 18 3V4H19Q19.825 4 20.413 4.588Q21 5.175 21 6V20Q21 20.825 20.413 21.413Q19.825 22 19 22H5ZM5 20H19Q19 20 19 20Q19 20 19 20V10H5V20Q5 20 5 20Q5 20 5 20ZM5 8H19V6Q19 6 19 6Q19 6 19 6H5Q5 6 5 6Q5 6 5 6V8ZM12 14Q11.575 14 11.288 13.713Q11 13.425 11 13Q11 12.575 11.288 12.287Q11.575 12 12 12Q12.425 12 12.713 12.287Q13 12.575 13 13Q13 13.425 12.713 13.713Q12.425 14 12 14ZM8 14Q7.575 14 7.288 13.713Q7 13.425 7 13Q7 12.575 7.288 12.287Q7.575 12 8 12Q8.425 12 8.713 12.287Q9 12.575 9 13Q9 13.425 8.713 13.713Q8.425 14 8 14ZM16 14Q15.575 14 15.288 13.713Q15 13.425 15 13Q15 12.575 15.288 12.287Q15.575 12 16 12Q16.425 12 16.713 12.287Q17 12.575 17 13Q17 13.425 16.713 13.713Q16.425 14 16 14ZM12 18Q11.575 18 11.288 17.713Q11 17.425 11 17Q11 16.575 11.288 16.287Q11.575 16 12 16Q12.425 16 12.713 16.287Q13 16.575 13 17Q13 17.425 12.713 17.713Q12.425 18 12 18ZM8 18Q7.575 18 7.288 17.713Q7 17.425 7 17Q7 16.575 7.288 16.287Q7.575 16 8 16Q8.425 16 8.713 16.287Q9 16.575 9 17Q9 17.425 8.713 17.713Q8.425 18 8 18ZM16 18Q15.575 18 15.288 17.713Q15 17.425 15 17Q15 16.575 15.288 16.287Q15.575 16 16 16Q16.425 16 16.713 16.287Q17 16.575 17 17Q17 17.425 16.713 17.713Q16.425 18 16 18Z" fill="currentColor"/>
              </svg>
              {(filters.startDate || filters.endDate) ? 'Date Range Applied' : 'Select Date Range'}
              {(filters.startDate || filters.endDate) && (
                <span className="filter-indicator">●</span>
              )}
            </button>
            
            {showDatePicker && (
              <div className="date-picker-dropdown">
                <div className="date-picker-content">
                  <div className="date-input-group">
                    <label>Start Date:</label>
            <input
              type="date"
              value={tempDateRange.startDate}
                      onChange={(e) => handleTempDateChange("startDate", e.target.value)}
                      className="date-input"
            />
                  </div>
                  <div className="date-input-group">
                    <label>End Date:</label>
            <input
              type="date"
              value={tempDateRange.endDate}
                      onChange={(e) => handleTempDateChange("endDate", e.target.value)}
                      className="date-input"
                    />
                  </div>
                  <div className="date-picker-actions">
                    <button onClick={clearDateRange} className="btn-secondary">Clear</button>
                    <button onClick={handleDateRangeApply} className="btn-primary">Apply</button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sort Menu */}
          <div className="sort-container">
            <button 
              className={`sort-btn ${showSortMenu ? 'active' : ''}`}
              onClick={handleSortToggle}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 18H9V16H3V18ZM3 6V8H21V6H3ZM3 13H15V11H3V13Z" fill="currentColor"/>
              </svg>
              Sort
            </button>
            
            {showSortMenu && (
              <div className="sort-dropdown">
                <div className="sort-content">
                  <button 
                    onClick={() => handleSort('started_at')}
                    className={sortBy === 'started_at' ? 'sort-option active' : 'sort-option'}
                  >
                    {getSortDisplay('started_at')}
                  </button>
                  <button 
                    onClick={() => handleSort('duration_seconds')}
                    className={sortBy === 'duration_seconds' ? 'sort-option active' : 'sort-option'}
                  >
                    {getSortDisplay('duration_seconds')}
                  </button>
                  <button 
                    onClick={() => handleSort('direction')}
                    className={sortBy === 'direction' ? 'sort-option active' : 'sort-option'}
                  >
                    {getSortDisplay('direction')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="table-container">
        <table className="calls-table calls-table-extended">
          <thead>
            <tr>
              <th>Type</th>
              <th>From</th>
              <th>To</th>
              <th>Call elapsed</th>
              <th>Status</th>
              <th>Time Stamp</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" className="loading-cell">Loading calls...</td>
              </tr>
            ) : calls.length === 0 ? (
              <tr>
                <td colSpan="6" className="no-data-cell">No calls found</td>
              </tr>
            ) : (
              calls.map((call) => (
                <tr 
                  key={call.id} 
                  className="call-row" 
                  onClick={() => handleCallClick(call)}
                >
                  <td>{getCallTypeBadge(call)}</td>
                  <td className="phone-number">
                    {renderPhoneWithRiaBadge(call.from_number)}
                  </td>
                  <td className="phone-number">
                    {renderPhoneWithRiaBadge(call.to_number)}
                  </td>
                  <td className="duration">{formatDuration(call.duration_seconds)}</td>
                  <td className="status">{getCallStatusBadge(call.status)}</td>
                  <td className="timestamp">{formatDate(call.started_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!loading && renderPagination()}
    </div>
  );
};

export default AdminActivityPage; 